import React, { useState, useMemo } from "react";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import useEditCandidateList from "./useEditCandidateList";
import { colors } from "../../../../../theme/colors";

import {
  ListContainer,
  ListHeaderContainer,
  HeaderTitleRow,
  SearchBox,
  StyledInputBase,
  StyledList,
  StyledListItem,
  StyledAvatar,
  StyledListItemText,
  SkeletonListItem,
} from "./style";

const ListEditCandidateInfo = ({
  interview,
  handleChatClick,
  selectedCandidateId,
}) => {
  const { filteredData, isLoading } = useEditCandidateList(interview);
  const [searchTerm, setSearchTerm] = useState("");

  const displayData = useMemo(() => {
    if (!filteredData) return [];
    if (!searchTerm.trim()) return filteredData;
    const term = searchTerm.toLowerCase().trim();
    return filteredData.filter((candidate) => {
      const name = candidate?.candidateName?.toLowerCase() || "";
      const email = candidate?.candidateEmail?.toLowerCase() || "";
      const roleStr = (
        typeof candidate?.applyFor === "string"
          ? candidate.applyFor
          : candidate?.applyFor?.role || ""
      ).toLowerCase();
      return (
        name.includes(term) || email.includes(term) || roleStr.includes(term)
      );
    });
  }, [filteredData, searchTerm]);

  const SkeletonLoader = () => (
    <>
      {Array.from({ length: 7 }).map((_, index) => (
        <SkeletonListItem key={`skeleton-${index}`}>
          <ListItemAvatar sx={{ minWidth: 54 }}>
            <Skeleton variant="circular" width={42} height={42} />
          </ListItemAvatar>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={22} />
            <Skeleton variant="text" width="80%" height={18} />
          </Box>
        </SkeletonListItem>
      ))}
    </>
  );

  const getRoleTitle = (data) => {
    if (!data) return null;
    if (typeof data?.applyFor === "string") return data.applyFor;
    if (data?.applyFor?.role) return data.applyFor.role;
    if (Array.isArray(data?.applyFor) && data?.applyFor[0]?.role)
      return data.applyFor[0].role;
    return null;
  };

  const CandidateListItem = ({ data }) => {
    const isSelected = selectedCandidateId === data?._id;
    const hasProfilePic = !!data?.profilePic;
    const hasClickHandler = !!handleChatClick;
    const roleTitle = getRoleTitle(data);

    return (
      <StyledListItem
        alignItems="flex-start"
        onClick={() => handleChatClick && handleChatClick(data?._id)}
        isSelected={isSelected}
        hasClickHandler={hasClickHandler}
      >
        <ListItemAvatar sx={{ minWidth: 52 }}>
          <StyledAvatar
            alt={data?.candidateName || "Candidate"}
            src={data?.profilePic}
            hasProfilePic={hasProfilePic}
          >
            {!hasProfilePic && data?.candidateName
              ? data.candidateName.charAt(0).toUpperCase()
              : "C"}
          </StyledAvatar>
        </ListItemAvatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StyledListItemText
            primary={data?.candidateName || "Unknown Candidate"}
            secondary={data?.candidateEmail || "No Email"}
            isSelected={isSelected}
          />
          {roleTitle && (
            <Chip
              label={roleTitle}
              size="small"
              sx={{
                marginTop: "6px",
                height: "20px",
                fontSize: "0.7rem",
                fontWeight: 500,
                backgroundColor: isSelected ? colors.primary : "#e2e8f0",
                color: isSelected ? "#ffffff" : "#475569",
                borderRadius: "6px",
                maxWidth: "100%",
                "& .MuiChip-label": {
                  paddingLeft: "6px",
                  paddingRight: "6px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
          )}
        </Box>
      </StyledListItem>
    );
  };

  return (
    <ListContainer>
      <ListHeaderContainer>
        <HeaderTitleRow>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#0f172a" }}
          >
            Candidates
          </Typography>
          <Chip
            label={filteredData ? filteredData.length : 0}
            size="small"
            sx={{
              backgroundColor: colors.primaryLight,
              color: colors.primary,
              fontWeight: 700,
              fontSize: "0.75rem",
              height: "22px",
              border: `1px solid ${colors.primaryLightBorder}`,
            }}
          />
        </HeaderTitleRow>
        <SearchBox>
          <SearchIcon sx={{ color: "#94a3b8", fontSize: "1.1rem" }} />
          <StyledInputBase
            placeholder="Search candidate or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBox>
      </ListHeaderContainer>

      <StyledList>
        {isLoading ? (
          <SkeletonLoader />
        ) : displayData && displayData.length > 0 ? (
          displayData.map((data) => (
            <CandidateListItem key={data?._id || data?.id} data={data} />
          ))
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 16px",
              textAlign: "center",
              color: "#94a3b8",
            }}
          >
            <PersonSearchIcon sx={{ fontSize: "2.5rem", mb: 1, opacity: 0.6 }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: "#64748b" }}>
              {searchTerm ? "No matching candidates" : "No candidates available"}
            </Typography>
          </Box>
        )}
      </StyledList>
    </ListContainer>
  );
};

export default ListEditCandidateInfo;

